import {useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent} from 'react';
import {PRODUCT_NAME} from '../src/product';
import {accountHref, accountLocation} from '../src/account-routes';
import designPreviews from '../config/design-previews.json';
import {accountApiPath, platform, reportPlatformError} from './platform/transport';
import {ACCOUNT_DESTINATIONS} from './account-navigation';

export const APP_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
function browserCheckout(pathname: string): boolean {
  const path = pathname.slice(APP_BASE.length);
  return path === '/subscribe' || path === '/billing' || path.startsWith('/billing/');
}
function desktopRoute(pathname: string): boolean {
  const path = pathname.slice(APP_BASE.length);
  return ACCOUNT_DESTINATIONS.some(route => path === route.path || (route.path !== '/desktop' && path.startsWith(`${route.path}/`))) ||
    ['/setup', '/login', '/register', '/setup-login'].includes(path) || path === '/admin' || path.startsWith('/admin/');
}
export function currentLocation() {return accountLocation(window.location.pathname, window.location.search, APP_BASE);}
export function navigate(path: string, replace = false): void {
  if (!path.startsWith(`${APP_BASE}/`) && path !== APP_BASE) throw new Error(`Navigation must stay inside ${PRODUCT_NAME}.`);
  path = accountHref(path, APP_BASE);
  const target = new URL(path, window.location.origin);
  if (target.origin !== window.location.origin) throw new Error(`Navigation must stay inside ${PRODUCT_NAME}.`);
  const pathname = target.pathname;
  if (platform().kind === 'desktop') {
    if (!desktopRoute(pathname) && !browserCheckout(pathname)) {void platform().openPage(new URL(path, platform().publicOrigin).href).catch(reportPlatformError); return;}
  }
  if (Object.values(designPreviews).some(preview => 'passwordProtected' in preview && preview.passwordProtected
    && (pathname === `${APP_BASE}${preview.path}` || pathname.startsWith(`${APP_BASE}${preview.path}/`)))) {
    // The web server owns preview access, including navigation from an already open page.
    if (replace) window.location.replace(path);
    else window.location.assign(path);
    return;
  }
  if (replace) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Capture real anchors, including links rendered by Markdown. External content
// never replaces the trusted desktop interface or receives native capabilities.
document.addEventListener('click', event => {
  if (platform().kind !== 'desktop' || event.defaultPrevented || event.button !== 0) return;
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
  const href = link?.getAttribute('href');
  if (!link || !href || href.startsWith('#')) return;
  // App links own their navigation callbacks. Let React close menus or accept
  // a canceled navigation before applying the same desktop route boundary.
  if (link.hasAttribute('data-eightbots-route')) return;
  event.preventDefault();
  event.stopPropagation();
  try {
    const apiPath = accountApiPath(href);
    if (apiPath) {
      const action = link.hasAttribute('download') ? platform().saveFile(apiPath, link.download || undefined) : platform().openAccountPage(apiPath);
      void action.catch(reportPlatformError); return;
    }
    const url = new URL(href, platform().publicOrigin);
    if (!['https:', 'mailto:'].includes(url.protocol) || url.username || url.password) {
      throw new Error('This link cannot open from EightBots.');
    }
    if (url.origin === platform().publicOrigin && (desktopRoute(url.pathname) || browserCheckout(url.pathname))) {
      navigate(`${url.pathname}${url.search}${url.hash}`); return;
    }
    void platform().openPage(url.href).catch(reportPlatformError);
  } catch (error) {reportPlatformError(error);}
}, true);
export function usePath(): string {
  return useSyncExternalStore(listener => {
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, () => currentLocation().pathname, () => `${APP_BASE}/`);
}
export function useSearch(): string {
  return useSyncExternalStore(listener => {
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, () => currentLocation().search, () => '');
}
export function Link({href, onClick, ...props}: AnchorHTMLAttributes<HTMLAnchorElement> & {href: string}) {
  href = accountHref(href, APP_BASE);
  function click(event: MouseEvent<HTMLAnchorElement>): void {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0) return;
    if (platform().kind !== 'desktop' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || props.target)) return;
    event.preventDefault();
    if(!window.dispatchEvent(new CustomEvent('eightbots-auth-navigation',{cancelable:true,detail:{href}})))return;
    navigate(href);
  }
  return <a {...props} data-eightbots-route="" href={href} onClick={click}/>;
}
